import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../../context/AuthContext';
import { dashboardApi } from '../../services/api';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend);

const CATEGORY_LABELS = {
  clarity_of_rr: 'Clarity of R&R',
  team_synergy: 'Team Synergy',
  trust_in_manager: 'Trust in Manager',
  recognition: 'Recognition',
  work_life_balance: 'Work-Life Balance',
  growth_opportunity: 'Growth Opportunity',
  communication: 'Communication',
  psychological_safety: 'Psychological Safety'
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState(null);
  const [ptmCases, setPtmCases] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPTM, setExpandedPTM] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [sumData, catData, ptmData, sentData, scoresData] = await Promise.all([
        dashboardApi.getSummary(),
        dashboardApi.getCategories(),
        dashboardApi.getPTM(),
        dashboardApi.getSentiment(),
        dashboardApi.getScores()
      ]);
      setSummary(sumData);
      setCategories(catData);
      setPtmCases(ptmData);
      setSentiment(sentData);
      setScores(scoresData);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dashboardApi.refresh();
      setTimeout(() => {
        loadDashboard();
        setRefreshing(false);
      }, 8000);
    } catch (err) {
      console.error('Refresh failed:', err);
      setRefreshing(false);
    }
  };

  const handleActionStatusChange = async (actionId, newStatus) => {
    try {
      await dashboardApi.updateAction(actionId, { status: newStatus });
      setPtmCases(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          cases: prev.cases.map(c => ({
            ...c,
            actions: c.actions.map(a =>
              a.id === actionId ? { ...a, status: newStatus } : a
            )
          }))
        };
      });
    } catch (err) {
      console.error('Failed to update action:', err);
    }
  };

  const togglePTMExpand = (id) => {
    setExpandedPTM(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const noData = !summary?.available;

  const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
  const chartGridColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const chartTickColor = isDark ? '#64748b' : '#94a3b8';
  const accentColor = isDark ? '#10b981' : '#059669';

  // Mood history line chart with gradient fill
  const moodHistoryData = sentiment?.history?.length > 1 ? {
    labels: sentiment.history.map(h => new Date(h.run_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })).reverse(),
    datasets: [{
      label: 'Mood Score',
      data: sentiment.history.map(h => h.mood).reverse(),
      borderColor: accentColor,
      borderWidth: 2.5,
      backgroundColor: (ctx) => {
        if (!ctx.chart.chartArea) return 'rgba(16,185,129,0.1)';
        const g = ctx.chart.ctx.createLinearGradient(0, ctx.chart.chartArea.top, 0, ctx.chart.chartArea.bottom);
        g.addColorStop(0, 'rgba(16,185,129,0.2)');
        g.addColorStop(0.5, 'rgba(16,185,129,0.05)');
        g.addColorStop(1, 'rgba(16,185,129,0)');
        return g;
      },
      fill: true,
      tension: 0.45,
      pointBackgroundColor: accentColor,
      pointBorderColor: isDark ? '#0a0a0b' : '#fff',
      pointBorderWidth: 3,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointHoverBorderColor: accentColor,
      pointHoverBackgroundColor: isDark ? '#0a0a0b' : '#fff'
    }]
  } : null;

  // Category bar chart
  const categoryChartData = categories?.available ? {
    labels: categories.categories.map(c => CATEGORY_LABELS[c.category] || c.category),
    datasets: [{
      data: categories.categories.map(c => c.avg_score),
      backgroundColor: categories.categories.map((c, i) => {
        const n = categories.categories.length;
        if (i < Math.ceil(n / 3)) return accentColor;
        if (i >= n - Math.ceil(n / 3)) return '#ef4444';
        return '#6366f1';
      }),
      borderRadius: 8,
      barThickness: 22,
      borderSkipped: false
    }]
  } : null;

  // Sentiment donut
  const sentimentChartData = sentiment?.available ? {
    labels: ['Positive', 'Neutral', 'Negative'],
    datasets: [{
      data: [
        sentiment.distribution?.positive || 0,
        sentiment.distribution?.neutral || 0,
        sentiment.distribution?.negative || 0
      ],
      backgroundColor: [accentColor, '#6366f1', '#ef4444'],
      hoverBackgroundColor: ['#059669', '#4f46e5', '#dc2626'],
      borderWidth: 0,
      spacing: 4,
      borderRadius: 4
    }]
  } : null;

  const totalSentiment = sentiment?.available
    ? (sentiment.distribution?.positive || 0) + (sentiment.distribution?.neutral || 0) + (sentiment.distribution?.negative || 0)
    : 0;

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-loading-spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="dash dash-nosidebar">
      <main className="dash-main">
        {/* Top bar with tabs */}
        <header className="dash-topbar">
          <div className="dash-topbar-left">
            <h1>HR Analytics</h1>
            <div className="dash-tabs">
              <button className={`dash-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
              <button className={`dash-tab ${activeTab === 'people' ? 'active' : ''}`} onClick={() => setActiveTab('people')}>People</button>
              <button className={`dash-tab ${activeTab === 'ptm' ? 'active' : ''}`} onClick={() => setActiveTab('ptm')}>
                PTM{ptmCases?.cases?.length > 0 && <span className="dash-tab-badge">{ptmCases.cases.length}</span>}
              </button>
              <button className={`dash-tab ${activeTab === 'actions' ? 'active' : ''}`} onClick={() => setActiveTab('actions')}>Actions</button>
            </div>
            {summary?.available && (
              <span className="dash-topbar-date">
                Last analyzed {new Date(summary.run_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(summary.run_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <div className="dash-topbar-right">
            <button className={`dash-refresh-btn ${refreshing ? 'loading' : ''}`} onClick={handleRefresh} disabled={refreshing}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              {refreshing ? 'Analyzing...' : 'Refresh Data'}
            </button>
          </div>
        </header>

        {noData ? (
          <div className="dash-empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" width="80" height="80">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            <h2>No Analytics Data Yet</h2>
            <p>Run the first AI analysis to populate your HR dashboard with insights from team conversations.</p>
            <button className="dash-refresh-btn primary" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? 'Analyzing...' : 'Run First Analysis'}
            </button>
          </div>
        ) : (
          <div className="dash-content">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* KPI Row */}
                <div className="kpi-row">
                  <div className="kpi-card" data-accent="blue">
                    <div className="kpi-icon blue">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <div className="kpi-data">
                      <div className="kpi-label">Active Employees</div>
                      <div className="kpi-value">{summary.active_users}<span className="kpi-total">/{summary.total_users}</span></div>
                      <div className="kpi-trend up">{summary.response_rate}% response rate</div>
                    </div>
                  </div>

                  <div className="kpi-card" data-accent="green">
                    <div className="kpi-icon green">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                    </div>
                    <div className="kpi-data">
                      <div className="kpi-label">NPS Score</div>
                      <div className="kpi-value">{summary.nps_score > 0 ? '+' : ''}{summary.nps_score}</div>
                      <div className={`kpi-trend ${summary.nps_score > 30 ? 'up' : summary.nps_score > 0 ? 'neutral' : 'down'}`}>
                        {summary.nps_score > 30 ? 'Excellent' : summary.nps_score > 0 ? 'Good' : 'Needs work'}
                      </div>
                    </div>
                  </div>

                  <div className="kpi-card" data-accent="purple">
                    <div className="kpi-icon purple">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                    </div>
                    <div className="kpi-data">
                      <div className="kpi-label">Mood Score</div>
                      <div className="kpi-value">{summary.avg_mood_score.toFixed(1)}<span className="kpi-total">/5</span></div>
                      <div className="kpi-stars">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={i <= Math.round(summary.avg_mood_score) ? 'star-filled' : 'star-empty'}>&#9733;</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="kpi-card" data-accent="orange">
                    <div className="kpi-icon orange">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                    </div>
                    <div className="kpi-data">
                      <div className="kpi-label">Engagement</div>
                      <div className="kpi-value">{Math.round(summary.avg_engagement_score)}<span className="kpi-total">/100</span></div>
                      <div className="kpi-bar-wrap">
                        <div className="kpi-bar">
                          <div className="kpi-bar-fill" style={{ width: `${summary.avg_engagement_score}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="charts-row">
                  <div className="dash-card chart-wide">
                    <div className="dash-card-header">
                      <h3>Category Scores</h3>
                      <div className="card-header-legend">
                        <span className="legend-item"><span className="legend-dot" style={{background: accentColor}}></span>Top scoring</span>
                        <span className="legend-item"><span className="legend-dot" style={{background:'#ef4444'}}></span>Bottom scoring</span>
                      </div>
                    </div>
                    {categoryChartData ? (
                      <div className="chart-area bar-area">
                        <Bar data={categoryChartData} options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: { backgroundColor: isDark ? 'rgba(10,10,11,0.95)' : '#1e293b', titleColor: '#fff', bodyColor: '#94a3b8', borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#334155', borderWidth: 1, padding: 14, cornerRadius: 10, titleFont: { weight: '600' } }
                          },
                          scales: {
                            x: { min: 0, max: 5, grid: { color: chartGridColor }, ticks: { color: chartTickColor, font: { size: 11 } }, border: { display: false } },
                            y: { grid: { display: false }, ticks: { color: isDark ? '#94a3b8' : '#475569', font: { size: 12, weight: '500', family: "'Plus Jakarta Sans', sans-serif" } }, border: { display: false } }
                          }
                        }} />
                      </div>
                    ) : <div className="card-empty">No category data available</div>}
                  </div>

                  <div className="dash-card">
                    <div className="dash-card-header">
                      <h3>Sentiment Distribution</h3>
                    </div>
                    {sentimentChartData && totalSentiment > 0 ? (
                      <>
                        <div className="chart-area donut-area">
                          <Doughnut data={sentimentChartData} options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            cutout: '74%',
                            plugins: {
                              legend: { display: false },
                              tooltip: { backgroundColor: isDark ? 'rgba(10,10,11,0.95)' : '#1e293b', titleColor: '#fff', bodyColor: '#94a3b8', borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#334155', borderWidth: 1, padding: 14, cornerRadius: 10 }
                            }
                          }} />
                          <div className="donut-center">
                            <div className="donut-center-val">{totalSentiment}</div>
                            <div className="donut-center-label">Total</div>
                          </div>
                        </div>
                        <div className="sentiment-legend">
                          {[
                            { label: 'Positive', count: sentiment.distribution?.positive || 0, color: accentColor },
                            { label: 'Neutral', count: sentiment.distribution?.neutral || 0, color: '#6366f1' },
                            { label: 'Negative', count: sentiment.distribution?.negative || 0, color: '#ef4444' },
                          ].map(s => (
                            <div key={s.label} className="sentiment-legend-item">
                              <span className="legend-dot" style={{ background: s.color }}></span>
                              <span className="legend-label">{s.label}</span>
                              <span className="legend-count">{s.count}</span>
                            </div>
                          ))}
                        </div>
                        {sentiment.trends && (
                          <div className="trend-pills">
                            <span className="trend-pill up">&#8599; Improving: {sentiment.trends.improving || 0}</span>
                            <span className="trend-pill flat">&#8594; Stable: {sentiment.trends.stable || 0}</span>
                            <span className="trend-pill down">&#8600; Declining: {sentiment.trends.declining || 0}</span>
                          </div>
                        )}
                      </>
                    ) : <div className="card-empty">No sentiment data</div>}
                  </div>
                </div>

                {/* PTM Preview + Mood Trend */}
                <div className="charts-row">
                  {moodHistoryData && (
                    <div className="dash-card chart-wide">
                      <div className="dash-card-header"><h3>Mood Trend</h3></div>
                      <div className="chart-area line-area">
                        <Line data={moodHistoryData} options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: { backgroundColor: isDark ? 'rgba(10,10,11,0.95)' : '#1e293b', titleColor: '#fff', bodyColor: '#94a3b8', borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#334155', borderWidth: 1, padding: 14, cornerRadius: 10, intersect: false, mode: 'index' }
                          },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: chartTickColor, font: { size: 11 } }, border: { display: false } },
                            y: { min: 0, max: 5, grid: { color: chartGridColor }, ticks: { color: chartTickColor, stepSize: 1 }, border: { display: false } }
                          },
                          interaction: { mode: 'nearest', axis: 'x', intersect: false }
                        }} />
                      </div>
                    </div>
                  )}

                  <div className="dash-card">
                    <div className="dash-card-header">
                      <h3>High Risk Employees</h3>
                      {ptmCases?.cases?.length > 0 && <span className="header-badge red">{ptmCases.cases.length}</span>}
                    </div>
                    {ptmCases?.cases?.length > 0 ? (
                      <div className="ptm-preview-list">
                        {ptmCases.cases.slice(0, 5).map(c => (
                          <div key={c.id} className="ptm-preview-item" onClick={() => { setActiveTab('ptm'); togglePTMExpand(c.id); }}>
                            <div className="ptm-avatar">{c.name?.charAt(0)}</div>
                            <div className="ptm-preview-info">
                              <div className="ptm-preview-name">{c.name}</div>
                              <div className="ptm-preview-factor">{(c.risk_factors || [])[0] || 'At risk'}</div>
                            </div>
                            <span className={`pill pill-${c.risk_level}`}>{c.risk_level}</span>
                          </div>
                        ))}
                        {ptmCases.cases.length > 5 && (
                          <button className="ptm-view-all" onClick={() => setActiveTab('ptm')}>View all {ptmCases.cases.length} cases</button>
                        )}
                      </div>
                    ) : <div className="card-empty">No high-risk employees detected</div>}
                  </div>
                </div>
              </>
            )}

            {/* People Scores Tab */}
            {activeTab === 'people' && (
              <div className="dash-card full-width">
                <div className="dash-card-header">
                  <h3>Individual Employee Scores</h3>
                  <span className="header-badge blue">{scores?.scores?.length || 0} employees</span>
                </div>
                {scores?.scores?.length > 0 ? (
                  <div className="data-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Mood</th>
                          <th>Engagement</th>
                          <th>NPS</th>
                          <th>Sentiment</th>
                          <th>Trend</th>
                          <th>Messages</th>
                          <th>Active Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scores.scores.map(s => (
                          <tr key={s.user_id}>
                            <td>
                              <div className="table-user">
                                <div className="table-avatar">{s.name?.charAt(0)}</div>
                                <div>
                                  <div className="table-name">{s.name}</div>
                                  <div className="table-email">{s.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className={`score-pill ${s.mood_score >= 3.5 ? 'green' : s.mood_score >= 2.5 ? 'yElow' : 'red'}`}>{s.mood_score?.toFixed(1)}</span></td>
                            <td>
                              <div className="mini-bar-wrap">
                                <div className="mini-bar"><div className="mini-bar-fill" style={{width: `${s.engagement_score}%`}}></div></div>
                                <span>{Math.round(s.engagement_score)}</span>
                              </div>
                            </td>
                            <td><span className={`pill pill-${s.nps_response === 1 ? 'promoter' : s.nps_response === -1 ? 'detractor' : 'passive'}`}>{s.nps_response === 1 ? 'Promoter' : s.nps_response === -1 ? 'Detractor' : 'Passive'}</span></td>
                            <td><span className={`pill pill-${s.sentiment_label}`}>{s.sentiment_label}</span></td>
                            <td><span className={`trend-indicator ${s.sentiment_trend}`}>{s.sentiment_trend === 'improving' ? '↗' : s.sentiment_trend === 'declining' ? '↘' : '→'} {s.sentiment_trend}</span></td>
                            <td>{s.message_count}</td>
                            <td>{s.active_days}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="card-empty">No score data available</div>}
              </div>
            )}

            {/* PTM Cases Tab */}
            {activeTab === 'ptm' && (
              <div className="dash-card full-width">
                <div className="dash-card-header">
                  <h3>People to Meet - Risk Cases</h3>
                  {ptmCases?.cases?.length > 0 && <span className="header-badge red">{ptmCases.cases.length} flagged</span>}
                </div>
                {ptmCases?.cases?.length > 0 ? (
                  <div className="ptm-full-list">
                    {ptmCases.cases.map(c => (
                      <div key={c.id} className={`ptm-full-item ${expandedPTM[c.id] ? 'expanded' : ''}`}>
                        <div className="ptm-full-row" onClick={() => togglePTMExpand(c.id)}>
                          <div className="ptm-full-left">
                            <div className="ptm-avatar-lg">{c.name?.charAt(0)}</div>
                            <div>
                              <div className="ptm-full-name">{c.name}</div>
                              <div className="ptm-full-email">{c.email}</div>
                            </div>
                          </div>
                          <div className="ptm-full-meta">
                            <span className={`pill pill-${c.risk_level}`}>{c.risk_level} risk</span>
                            <span className="ptm-mood-badge">Mood: {c.mood_score?.toFixed(1)}/5</span>
                            <span className={`trend-indicator ${c.sentiment_trend}`}>{c.sentiment_trend}</span>
                          </div>
                          <svg className={`ptm-expand-icon ${expandedPTM[c.id] ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </div>
                        {expandedPTM[c.id] && (
                          <div className="ptm-full-details">
                            <div className="ptm-detail-section">
                              <h4>Risk Factors</h4>
                              <div className="ptm-tags">{(c.risk_factors || []).map((f, i) => <span key={i} className="ptm-tag red">{f}</span>)}</div>
                            </div>
                            <div className="ptm-detail-section">
                              <h4>Behavioral Patterns</h4>
                              <div className="ptm-tags">{(c.patterns || []).map((p, i) => <span key={i} className="ptm-tag purple">{p}</span>)}</div>
                            </div>
                            {c.actions?.length > 0 && (
                              <div className="ptm-detail-section">
                                <h4>Recommended Actions</h4>
                                <div className="ptm-actions-grid">
                                  {c.actions.map(a => (
                                    <div key={a.id} className="ptm-action-card">
                                      <div className="ptm-action-top">
                                        <span className={`pill pill-priority-${a.priority}`}>{a.priority}</span>
                                        <span className="ptm-action-cat">{a.category}</span>
                                      </div>
                                      <div className="ptm-action-text">{a.action_text}</div>
                                      <select className="ptm-action-select" value={a.status} onChange={(e) => handleActionStatusChange(a.id, e.target.value)}>
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                      </select>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <div className="card-empty">No PTM cases flagged</div>}
              </div>
            )}

            {/* Actions Tab */}
            {activeTab === 'actions' && (
              <div className="dash-card full-width">
                <div className="dash-card-header"><h3>AI-Generated Action Items</h3></div>
                {ptmCases?.cases?.some(c => c.actions?.length > 0) ? (
                  <div className="data-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr><th>Employee</th><th>Action</th><th>Priority</th><th>Category</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {ptmCases.cases.flatMap(c => (c.actions || []).map(a => (
                          <tr key={a.id}>
                            <td><div className="table-user"><div className="table-avatar">{c.name?.charAt(0)}</div><span className="table-name">{c.name}</span></div></td>
                            <td className="action-cell">{a.action_text}</td>
                            <td><span className={`pill pill-priority-${a.priority}`}>{a.priority}</span></td>
                            <td><span className="action-cat-label">{a.category}</span></td>
                            <td>
                              <select className="ptm-action-select" value={a.status} onChange={(e) => handleActionStatusChange(a.id, e.target.value)}>
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </select>
                            </td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="card-empty">No action items generated yet</div>}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
